export const CHAT_STORAGE_CACHE_VERSION = "v1";

type CacheValue = Record<string, unknown>;

export type ChatCacheEnvelope<T> = {
  value: T;
  fetchedAt: number;
};

export type StoredTimestamp = {
  seconds: number;
  nanoseconds: number;
};

export type HydratedTimestamp = StoredTimestamp & {
  toMillis: () => number;
  toDate: () => Date;
};

const isBrowserStorageAvailable = () => {
  return typeof window !== "undefined" && Boolean(window.localStorage);
};

const encodeKeyPart = (part: string | number) => {
  return encodeURIComponent(String(part));
};

export const buildChatCacheKey = (
  ...parts: Array<string | number | null | undefined>
) => {
  const keyParts = parts
    .filter((part): part is string | number => part !== null && part !== undefined)
    .map(encodeKeyPart);

  return ["chat", CHAT_STORAGE_CACHE_VERSION, ...keyParts].join(":");
};

export const readChatCache = <T>(key: string): T | null => {
  if (!isBrowserStorageAvailable()) return null;

  const item = window.localStorage.getItem(key);
  if (!item) return null;

  try {
    return JSON.parse(item) as T;
  } catch {
    window.localStorage.removeItem(key);
    return null;
  }
};

export const writeChatCache = <T>(key: string, value: T): boolean => {
  if (!isBrowserStorageAvailable()) return false;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};

export const removeChatCache = (key: string) => {
  if (!isBrowserStorageAvailable()) return;
  window.localStorage.removeItem(key);
};

export const isChatCacheFresh = (fetchedAt: number | undefined, ttlMs: number) => {
  return typeof fetchedAt === "number" && Date.now() - fetchedAt < ttlMs;
};

export const readFreshChatCache = <T>(
  key: string,
  ttlMs: number
): ChatCacheEnvelope<T> | null => {
  const cached = readChatCache<ChatCacheEnvelope<T>>(key);
  if (!cached || !isChatCacheFresh(cached.fetchedAt, ttlMs)) return null;
  return cached;
};

export const writeFreshChatCache = <T>(key: string, value: T): boolean => {
  return writeChatCache<ChatCacheEnvelope<T>>(key, {
    value,
    fetchedAt: Date.now(),
  });
};

export const serializeTimestamp = (value: unknown): StoredTimestamp | null => {
  if (!value) return null;

  if (value instanceof Date) {
    const millis = value.getTime();
    return {
      seconds: Math.floor(millis / 1000),
      nanoseconds: (millis % 1000) * 1000000,
    };
  }

  if (typeof value === "number") {
    return {
      seconds: Math.floor(value / 1000),
      nanoseconds: (value % 1000) * 1000000,
    };
  }

  if (typeof value !== "object") return null;

  const timestamp = value as CacheValue;
  const seconds = timestamp.seconds;
  const nanoseconds = timestamp.nanoseconds ?? timestamp.nanosecond ?? 0;

  if (typeof seconds !== "number" || typeof nanoseconds !== "number") {
    return null;
  }

  return { seconds, nanoseconds };
};

export const deserializeTimestamp = (
  value: StoredTimestamp | null | undefined
): HydratedTimestamp | null => {
  if (!value) return null;

  return {
    seconds: value.seconds,
    nanoseconds: value.nanoseconds,
    toMillis: () =>
      value.seconds * 1000 + Math.floor(value.nanoseconds / 1000000),
    toDate: () =>
      new Date(value.seconds * 1000 + Math.floor(value.nanoseconds / 1000000)),
  };
};

export const getTimestampMillis = (value: unknown): number | null => {
  const timestamp = serializeTimestamp(value);
  if (!timestamp) return null;
  return timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1000000);
};

export const mergeById = <T extends { id: string }>(
  existing: T[],
  incoming: T[]
) => {
  const byId = new Map<string, T>();

  existing.forEach((item) => byId.set(item.id, item));
  incoming.forEach((item) => byId.set(item.id, item));

  return Array.from(byId.values());
};

export const capItems = <T>(items: T[], limit: number) => {
  if (limit <= 0) return [];
  return items.slice(0, limit);
};
